/**
 * SUNTRONIX ONE — Support Desk Inbox + Case detail.
 * Phase-1 depth-first: case list, filter by status/priority, create-case
 * dialog, and case detail with the 15-status state-machine action bar.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, PlusCircle, Search, LifeBuoy, ShieldCheck, AlertTriangle,
  ArrowLeft, Send,
} from "lucide-react";

/* ------------------------------------------------------------------------ */
/*  Priority + status style tokens (Vibrant Calm palette)                    */
/* ------------------------------------------------------------------------ */
const PRIORITY_STYLE = {
  P1: { bg: "#FFF0F2", fg: "#C2333F" },
  P2: { bg: "#FFF8E7", fg: "#B25A00" },
  P3: { bg: "#EFF5FF", fg: "#246BFD" },
  P4: { bg: "#F2F4F7", fg: "#667085" },
};
const STATUS_STYLE = {
  NEW:                { bg: "#EFF5FF", fg: "#246BFD" },
  ACKNOWLEDGED:       { bg: "#ECFAFF", fg: "#086F8E" },
  DIAGNOSING:         { bg: "#F5F0FF", fg: "#805AD5" },
  REMOTE_SUPPORT:     { bg: "#F5F0FF", fg: "#805AD5" },
  ONSITE_REQUIRED:    { bg: "#FFF8E7", fg: "#B25A00" },
  ENGINEER_ASSIGNED:  { bg: "#E6F7F8", fg: "#0B7583" },
  PART_REQUIRED:      { bg: "#FFF8E7", fg: "#B25A00" },
  OEM_RMA:            { bg: "#FFF8E7", fg: "#B25A00" },
  LOANER_REQUIRED:    { bg: "#FFF8E7", fg: "#B25A00" },
  REPAIR_IN_PROGRESS: { bg: "#E6F7F8", fg: "#0B7583" },
  CUSTOMER_ACTION:    { bg: "#FFF0F2", fg: "#C2333F" },
  EMPLOYEE_ACTION:    { bg: "#FFF0F2", fg: "#C2333F" },
  RESOLVED:           { bg: "#ECFDF6", fg: "#169C70" },
  CLOSED:             { bg: "#F2F4F7", fg: "#667085" },
  CANCELLED:          { bg: "#F2F4F7", fg: "#667085" },
  VOID:               { bg: "#F2F4F7", fg: "#667085" },
};
const NEXT_STATUS_MAP = {
  NEW:                ["ACKNOWLEDGED", "DIAGNOSING", "CANCELLED"],
  ACKNOWLEDGED:       ["DIAGNOSING", "REMOTE_SUPPORT", "ONSITE_REQUIRED"],
  DIAGNOSING:         ["REMOTE_SUPPORT", "PART_REQUIRED", "OEM_RMA",
                        "LOANER_REQUIRED", "REPAIR_IN_PROGRESS", "RESOLVED"],
  REMOTE_SUPPORT:     ["RESOLVED", "ONSITE_REQUIRED", "CUSTOMER_ACTION"],
  ONSITE_REQUIRED:    ["ENGINEER_ASSIGNED", "REPAIR_IN_PROGRESS"],
  ENGINEER_ASSIGNED:  ["REPAIR_IN_PROGRESS", "PART_REQUIRED", "RESOLVED"],
  PART_REQUIRED:      ["REPAIR_IN_PROGRESS", "OEM_RMA", "RESOLVED"],
  OEM_RMA:            ["REPAIR_IN_PROGRESS", "RESOLVED"],
  LOANER_REQUIRED:    ["REPAIR_IN_PROGRESS", "RESOLVED"],
  REPAIR_IN_PROGRESS: ["RESOLVED", "PART_REQUIRED", "OEM_RMA"],
  CUSTOMER_ACTION:    ["DIAGNOSING", "RESOLVED", "CANCELLED"],
  EMPLOYEE_ACTION:    ["DIAGNOSING", "RESOLVED", "CANCELLED"],
  RESOLVED:           ["CLOSED"],
  CLOSED:             [],
  CANCELLED:          [],
  VOID:               [],
};

const Pill = ({ style, children, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                                          text-[10px] font-semibold tracking-wide"
        style={{ background: style.bg, color: style.fg }}>{children}</span>
);

/* ------------------------------------------------------------------------ */
/*  INBOX                                                                    */
/* ------------------------------------------------------------------------ */
export function SupportInbox() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (priority) params.set("priority", priority);
      const [{ data }, { data: s }] = await Promise.all([
        api.get(`/support/cases?${params.toString()}`),
        api.get("/support/summary"),
      ]);
      setRows(data.items || []);
      setSummary(s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [q, status, priority]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="support-inbox">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
            Operations
          </div>
          <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
            Support Desk
          </h1>
          <p className="text-[13px] text-[#667085] mt-1 max-w-2xl">
            Every device issue, warranty question, engineer visit and RMA — one
            case, one asset, one chain of custody.
          </p>
        </div>
        <Button className="bg-brand-blue hover:bg-brand-blueh"
                onClick={() => setDialogOpen(true)}
                data-testid="support-new-case">
          <PlusCircle className="h-4 w-4 mr-1.5" /> New support case
        </Button>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Open" value={summary.open_total} tone="blue" />
          <Kpi label="P1 open"        value={summary.p1_open}      tone="coral" />
          <Kpi label="P2 open"        value={summary.p2_open}      tone="amber" />
          <Kpi label="Awaiting parts" value={summary.awaiting_parts} tone="amber" />
          <Kpi label="SLA breached"   value={summary.sla_breached} tone="coral" />
          <Kpi label="Resolved today" value={summary.resolved_today} tone="mint" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-md p-2">
        <Search className="h-3.5 w-3.5 text-[#98A2B3] ml-1" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search case #, serial, title, requester…"
                data-testid="support-search"
                className="h-8 w-72" />
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="support-filter-status"
                className="h-8 border border-slate-200 rounded px-2 text-[12px] bg-white">
          <option value="">Any status</option>
          {Object.keys(STATUS_STYLE).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)}
                data-testid="support-filter-priority"
                className="h-8 border border-slate-200 rounded px-2 text-[12px] bg-white">
          <option value="">Any priority</option>
          {["P1", "P2", "P3", "P4"].map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
            <tr>
              <th className="text-left px-3 py-2">Case</th>
              <th className="text-left px-3 py-2">Priority</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Requester</th>
              <th className="text-left px-3 py-2">Serial</th>
              <th className="text-left px-3 py-2">SLA target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[#98A2B3]">
                <Loader2 className="h-4 w-4 mx-auto animate-spin" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#98A2B3] text-[13px]">
                <LifeBuoy className="h-5 w-5 mx-auto mb-2 opacity-50" />
                No support cases match these filters.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 cursor-pointer"
                  data-testid={`support-row-${r.case_number}`}>
                <td className="px-3 py-2">
                  <Link to={`/support/${r.id}`} className="font-mono text-xs text-brand-blue hover:underline">
                    {r.case_number}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <Pill style={PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.P3}>
                    {r.priority}
                  </Pill>
                </td>
                <td className="px-3 py-2">
                  <Pill style={STATUS_STYLE[r.status] || STATUS_STYLE.NEW}>
                    {r.status}
                  </Pill>
                </td>
                <td className="px-3 py-2 text-[13px] text-[#101828]">{r.title}</td>
                <td className="px-3 py-2 text-[12px] text-[#667085]">{r.requester_email || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-[#667085]">{r.serial || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-[#667085]">
                  {r.sla_target_at ? new Date(r.sla_target_at).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewCaseDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const map = {
    blue:  { bg: "#EFF5FF", accent: "#246BFD" },
    coral: { bg: "#FFF0F2", accent: "#C2333F" },
    amber: { bg: "#FFF8E7", accent: "#B25A00" },
    mint:  { bg: "#ECFDF6", accent: "#169C70" },
  };
  const t = map[tone] || map.blue;
  return (
    <div className="rounded-md border border-slate-200 p-3 relative overflow-hidden"
         style={{ background: `linear-gradient(180deg, ${t.bg} 0%, #FFFFFF 60%)` }}
         data-testid={`support-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: t.accent }} />
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-[#101828]">{value ?? 0}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/*  NEW CASE DIALOG                                                          */
/* ------------------------------------------------------------------------ */
function NewCaseDialog({ open, onOpenChange, onSaved }) {
  const empty = useMemo(() => ({
    title: "", issue: "", category: "software", sub_category: "",
    serial: "", requester_email: "", requester_name: "",
    location: "", user_urgency: "medium", impact: "medium",
    master_company_id: "",
  }), []);
  const [form, setForm] = useState(empty);
  const [masters, setMasters] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(empty);
    api.get("/master-companies")
      .then(({ data }) => setMasters(data.items || []))
      .catch(() => {});
  }, [empty, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.issue.trim()) {
      toast.error("Title and issue description are required"); return;
    }
    if (!form.requester_email.trim()) {
      toast.error("Requester email is required"); return;
    }
    const payload = { ...form };
    if (!payload.master_company_id) delete payload.master_company_id;
    setSaving(true);
    try {
      const { data } = await api.post("/support/cases", payload);
      toast.success(`${data.case_number} created · ${data.priority}`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="support-new-case-dialog">
        <DialogHeader><DialogTitle>New support case</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Title" testId="new-title"
              value={form.title} onChange={(v) => set("title", v)} span={2} />
          <F label="Description of the issue" testId="new-issue" span={2} textarea
              value={form.issue} onChange={(v) => set("issue", v)} />
          {masters.length > 0 && (
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
                Customer (required for Suntronix operators)
              </label>
              <select value={form.master_company_id}
                       onChange={(e) => set("master_company_id", e.target.value)}
                       data-testid="new-master"
                       className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
                <option value="">— my tenant —</option>
                {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          <SelectF label="Category" testId="new-category"
              value={form.category} onChange={(v) => set("category", v)}
              options={["outage", "vip_user", "data_loss", "device_down",
                          "hardware_failure", "security", "peripheral",
                          "software", "how_to", "cosmetic"]} />
          <F label="Sub-category (optional)" testId="new-sub"
              value={form.sub_category} onChange={(v) => set("sub_category", v)} />
          <F label="Requester email" testId="new-email"
              value={form.requester_email} onChange={(v) => set("requester_email", v)} />
          <F label="Requester name (optional)" testId="new-name"
              value={form.requester_name} onChange={(v) => set("requester_name", v)} />
          <F label="Serial (optional)" testId="new-serial"
              value={form.serial} onChange={(v) => set("serial", v)} />
          <F label="Location (optional)" testId="new-location"
              value={form.location} onChange={(v) => set("location", v)} />
          <SelectF label="Urgency (hint)" testId="new-urgency"
              value={form.user_urgency} onChange={(v) => set("user_urgency", v)}
              options={["low", "medium", "high"]} />
          <SelectF label="Impact" testId="new-impact"
              value={form.impact} onChange={(v) => set("impact", v)}
              options={["low", "medium", "high"]} />
        </div>
        <div className="text-[11px] text-[#667085] leading-relaxed">
          <ShieldCheck className="h-3 w-3 inline mr-1 text-brand-mint" />
          Priority is resolved from category + impact + urgency using the SLA
          engine — never solely from user-provided urgency.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="new-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-brand-blue hover:bg-brand-blueh"
                    data-testid="new-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Open case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, testId, span = 1, textarea }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{label}</label>
      {textarea
        ? <Textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
                     data-testid={testId} className="mt-1" />
        : <Input value={value} onChange={(e) => onChange(e.target.value)}
                  data-testid={testId} className="mt-1" />}
    </div>
  );
}
function SelectF({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testId}
              className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/*  CASE DETAIL                                                              */
/* ------------------------------------------------------------------------ */
export function SupportCase() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("");
  const [billable, setBillable] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/support/cases/${caseId}`);
      setC(data);
      setBillable(!!data.billable);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const nextStatuses = useMemo(() => NEXT_STATUS_MAP[c?.status] || [], [c]);

  const transition = async (status) => {
    setTransitioning(true);
    try {
      const body = { status, note: note || undefined };
      if (status === "RESOLVED") {
        body.resolution = resolution || undefined;
        body.billable = billable;
      }
      const { data } = await api.post(`/support/cases/${caseId}/transition`, body);
      setC(data); setNote(""); setResolution("");
      toast.success(`Moved to ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTransitioning(false); }
  };

  if (busy && !c) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!c) return null;

  const warrantyStyle = c.warranty?.status === "COVERED"
    ? "text-brand-mint" : c.warranty?.status === "NOT_COVERED"
    ? "text-brand-coral" : "text-brand-amber";

  return (
    <div className="max-w-[1200px] space-y-4" data-testid="support-case">
      <button onClick={() => nav("/support")}
              className="text-[12px] text-[#667085] hover:text-[#101828] inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Back to inbox
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-[#667085]">{c.case_number}</span>
            <Pill style={PRIORITY_STYLE[c.priority] || PRIORITY_STYLE.P3}>
              {c.priority}
            </Pill>
            <Pill style={STATUS_STYLE[c.status] || STATUS_STYLE.NEW}
                   testId="case-status">
              {c.status}
            </Pill>
          </div>
          <h1 className="mt-2 text-xl font-semibold text-[#101828]">{c.title}</h1>
          <p className="mt-1 text-[13px] text-[#667085] whitespace-pre-line">{c.issue}</p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <Meta label="Requester" value={c.requester_email} />
            <Meta label="Serial"    value={c.serial || "—"} />
            <Meta label="Category"  value={c.category} />
            <Meta label="SLA target" value={c.sla_target_at
              ? new Date(c.sla_target_at).toLocaleString() : "—"} />
          </div>
        </div>
        {c.warranty && (
          <div className="w-56 border-l border-slate-100 pl-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Warranty</div>
            <div className={`mt-1 font-semibold ${warrantyStyle}`}
                 data-testid="case-warranty">
              {(c.warranty.status || "").replace(/_/g, " ")}
            </div>
            {c.warranty.warranty_end && (
              <div className="text-[11px] text-[#667085] mt-1">
                Ends: {new Date(c.warranty.warranty_end).toLocaleDateString()}
              </div>
            )}
            {c.warranty.reason && (
              <div className="text-[11px] text-[#667085] mt-1">{c.warranty.reason}</div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {nextStatuses.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4"
             data-testid="case-actions">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-2">
            Advance case
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                     rows={2} placeholder="Optional note recorded on the timeline…"
                     data-testid="case-note" />
          {nextStatuses.includes("RESOLVED") && (
            <>
              <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)}
                         rows={2} placeholder="Resolution (required when marking Resolved)…"
                         data-testid="case-resolution" className="mt-2" />
              <label className="flex items-center gap-2 mt-2 text-[12px]">
                <input type="checkbox" checked={billable}
                       onChange={(e) => setBillable(e.target.checked)}
                       data-testid="case-billable" />
                Flag as billable — creates a billing candidate on Resolve
              </label>
            </>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {nextStatuses.map((s) => (
              <Button key={s} onClick={() => transition(s)} disabled={transitioning}
                      className={s === "RESOLVED"
                        ? "bg-brand-recycle hover:opacity-90"
                        : "bg-brand-blue hover:bg-brand-blueh"}
                      data-testid={`case-transition-${s}`}
                      size="sm">
                <Send className="h-3 w-3 mr-1" /> {s.replace(/_/g, " ")}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white border border-slate-200 rounded-md p-4"
           data-testid="case-timeline">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-3">
          Timeline
        </div>
        <ol className="space-y-3">
          {(c.events || []).slice().reverse().map((ev, i) => (
            <li key={i} className="grid grid-cols-[110px_1fr] gap-3 text-[12px]">
              <div className="text-[#667085]">{new Date(ev.at).toLocaleString()}</div>
              <div>
                <div className="text-[#101828] font-medium">
                  {ev.kind === "case.opened"
                    ? `Case opened · ${ev.priority || ""}`
                    : `${(ev.from || "").replace(/_/g, " ")} → ${(ev.to || "").replace(/_/g, " ")}`}
                </div>
                {ev.note && <div className="text-[#667085] mt-0.5">{ev.note}</div>}
                <div className="text-[10px] text-[#98A2B3] mt-0.5">
                  by {ev.actor || "system"}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {c.resolution && (
        <div className="bg-white border border-slate-200 rounded-md p-4"
             data-testid="case-resolution-block">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085]">Resolution</div>
          <p className="mt-1 text-[13px] text-[#101828] whitespace-pre-line">{c.resolution}</p>
          {c.root_cause && (
            <>
              <div className="mt-2 text-[11px] uppercase tracking-[0.14em] text-[#667085]">Root cause</div>
              <p className="mt-1 text-[13px] text-[#101828] whitespace-pre-line">{c.root_cause}</p>
            </>
          )}
        </div>
      )}

      <ServiceActions caseData={c} onChanged={setC} />
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/*  Phase 1.5 · Dispatch engineer · Open RMA · Reserve loaner                */
/* ------------------------------------------------------------------------ */
function ServiceActions({ caseData, onChanged }) {
  const [engineers, setEngineers] = useState([]);
  const [engId, setEngId] = useState("");
  const [rmaOem, setRmaOem] = useState("");
  const [busy, setBusy] = useState(null);   // "dispatch" | "rma" | "loaner"

  useEffect(() => {
    api.get("/support/engineers").then(({ data }) => setEngineers(data.items || []))
      .catch(() => {});
  }, []);

  const doDispatch = async () => {
    if (!engId) { toast.error("Pick an engineer"); return; }
    setBusy("dispatch");
    try {
      const { data } = await api.post(`/support/cases/${caseData.id}/dispatch`,
        { engineer_id: engId });
      toast.success(`Engineer ${data.job.engineer_name} dispatched`);
      onChanged(data.case);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const openRma = async () => {
    if (!rmaOem.trim()) { toast.error("OEM name required"); return; }
    setBusy("rma");
    try {
      const { data } = await api.post("/support/rma", {
        case_id: caseData.id,
        oem: rmaOem.trim(),
        warranty_eligibility: caseData.warranty?.status === "COVERED"
          ? "eligible" : "review",
      });
      toast.success(`${data.rma.rma_number} opened with ${rmaOem}`);
      onChanged(data.case);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const reserveLoaner = async () => {
    setBusy("loaner");
    try {
      const { data } = await api.post(
        `/support/cases/${caseData.id}/loaner/reserve`, {});
      if (data.reserved) {
        toast.success(`Loaner ${data.loaner_serial} reserved · return by ${data.return_due_at?.slice(0,10)}`);
        onChanged(data.case);
      } else {
        toast.warning("No available loaner — demand recorded on the case");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 grid md:grid-cols-3 gap-4"
         data-testid="case-service-actions">
      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-1">
          Field Service
        </div>
        <select value={engId} onChange={(e) => setEngId(e.target.value)}
                data-testid="dispatch-engineer"
                className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] bg-white">
          <option value="">— pick engineer —</option>
          {engineers.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} {e.skills?.length ? `· ${e.skills.join(", ")}` : ""}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={doDispatch} disabled={busy === "dispatch"}
                data-testid="dispatch-submit"
                className="bg-brand-blue hover:bg-brand-blueh mt-2 w-full">
          Dispatch engineer
        </Button>
        {caseData.engineer_name && (
          <div className="text-[11px] text-[#169C70] mt-1">
            Assigned: {caseData.engineer_name}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-1">
          Warranty &amp; OEM RMA
        </div>
        <Input value={rmaOem} onChange={(e) => setRmaOem(e.target.value)}
                placeholder="OEM (Apple, Dell, HP…)"
                data-testid="rma-oem" className="h-9" />
        <Button size="sm" onClick={openRma} disabled={busy === "rma"}
                data-testid="rma-submit"
                className="bg-brand-blue hover:bg-brand-blueh mt-2 w-full">
          Open OEM RMA
        </Button>
        {caseData.rma_number && (
          <div className="text-[11px] text-[#667085] mt-1 font-mono">
            {caseData.rma_number}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-1">
          Loaner
        </div>
        <div className="text-[11px] text-[#667085] leading-relaxed mb-1">
          Reserve the first available pool device — return due in 14 days.
        </div>
        <Button size="sm" onClick={reserveLoaner} disabled={busy === "loaner"}
                data-testid="loaner-submit"
                className="bg-brand-blue hover:bg-brand-blueh w-full">
          Reserve loaner
        </Button>
        {caseData.loaner_serial && (
          <div className="text-[11px] text-[#169C70] mt-1 font-mono">
            {caseData.loaner_serial}
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#98A2B3]">{label}</div>
      <div className="text-[12px] text-[#101828] mt-0.5 break-all">{value}</div>
    </div>
  );
}
